import type { PublicCommentConfig } from "./types";

// Image extensions map to PollCommentAttachmentTypes.Image (0); everything else is treated as
// a Document (2). Mirrors the server-side enum so SaveComment routes storage correctly
// (images → Cloudinary/GCP, documents → ImportFile).
const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

export function extensionOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

export function attachmentTypeForExt(ext: string): number {
  return IMAGE_EXTS.includes(ext.toLowerCase()) ? 0 : 2; // Image vs Document
}

export function isImageExt(ext: string): boolean {
  return IMAGE_EXTS.includes(ext.toLowerCase());
}

export interface AttachmentValidation {
  ok: boolean;
  error?: string;
}

// Enforce the admin-configured allowlist, per-file size cap, and total count on the client.
// The server re-validates (see CommentsController.Comment) — this is UX, not the security boundary.
export function validateFiles(
  incoming: File[],
  existingCount: number,
  config: PublicCommentConfig
): AttachmentValidation {
  const allowed = (config.attachmentTypesAllowed || []).map((e) =>
    e.toLowerCase()
  );
  const maxCount =
    config.attachmentsLimitCount > 0 ? config.attachmentsLimitCount : Infinity;
  const maxSizeMb =
    config.attachmentsLimitSize > 0 ? config.attachmentsLimitSize : Infinity;

  if (existingCount + incoming.length > maxCount) {
    return {
      ok: false,
      error: `You can attach at most ${maxCount} file${
        maxCount === 1 ? "" : "s"
      }.`
    };
  }
  for (const f of incoming) {
    const ext = extensionOf(f.name);
    if (allowed.length > 0 && !allowed.includes(ext)) {
      return {
        ok: false,
        error: `"${f.name}" — that file type isn't allowed here.`
      };
    }
    if (f.size > maxSizeMb * 1024 * 1024) {
      return {
        ok: false,
        error: `"${f.name}" exceeds the ${maxSizeMb} MB limit.`
      };
    }
  }
  return { ok: true };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

// Shape matching the server's PollCommentAttachment (Base64 is a data URL; SaveComment strips the
// "data:...;base64," prefix and decodes the remainder).
export interface OutgoingAttachment {
  Type: number;
  FileName: string;
  Extension: string;
  Base64: string;
}

export async function buildCommentAttachments(
  files: File[]
): Promise<OutgoingAttachment[]> {
  const out: OutgoingAttachment[] = [];
  for (const f of files) {
    const ext = extensionOf(f.name);
    out.push({
      Type: attachmentTypeForExt(ext),
      FileName: f.name,
      Extension: ext,
      Base64: await fileToDataUrl(f)
    });
  }
  return out;
}
